/**
 * The simulation step: wires the sim modules in a FIXED order. Do not put gameplay logic here; each step lives in
 * its own module with one owner (see README.md "Module ownership").
 *
 * Contract:
 *  - `step(state, inputs, events)` advances EXACTLY one tick in place. `inputs[0]`, `inputs[1]` are the
 *    InputFrames of player 0 and 1 for this tick (sanitized by the caller; the sim masks again defensively).
 *  - Events for this tick are pushed into `events` with `tick = state.tick` (before the increment). ALWAYS pass a
 *    sink when the result is shown, rollback resimulation included (the session de-duplicates with `eventKey`).
 *    Pass null only for headless runs (sync-test, soak, determinism tests, AI lookahead).
 *  - No allocation in steady state, no DOM/timers/Math.random/non-deterministic Math (see state.ts invariants).
 *  - Order per tick:
 *      1. recordInputs            player/input.ts      (runs during hit-stop too; buffers freeze)
 *      2. hit-stop: hitStop--, rulesDuringHitStop, ++tick, return
 *      3. rulesPre                rules/pre.ts         phase timers, snap, rush count, offside
 *      4. updateSpecials          specials/update.ts   activation, telegraph, timers, decoy
 *         updateControllers       player/controller.ts state machines → velocities
 *      5. integrate               physics/integrate.ts players, push-apart, gloves
 *      6. updateBall              ball/update.ts       held / flight / catches / swats / posts / FG window / ground
 *      7. resolveContacts         contact/resolve.ts   COUNTER matrix, tackles, breaks, hit-stop, baits
 *      8. rulesPost               rules/post.ts        play end, scoring, downs, drives, game over
 *      9. updateCameraHint        camera.ts            ; ++tick
 */
import type { EventSink, InputFrame } from '@hf/shared';
import { updateBall } from './ball/update';
import { updateCameraHint } from './camera';
import { resolveContacts } from './contact/resolve';
import type { SimEvent } from './events';
import { integrate } from './physics/integrate';
import { updateControllers } from './player/controller';
import { recordInputs } from './player/input';
import { rulesPost } from './rules/post';
import { rulesDuringHitStop, rulesPre } from './rules/pre';
import { updateSpecials } from './specials/update';
import type { SimState } from './state';

export const step = (
  state: SimState,
  inputs: readonly InputFrame[],
  events: EventSink<SimEvent> | null,
): void => {
  recordInputs(state, inputs);
  if (state.hitStop > 0) {
    state.hitStop--;
    rulesDuringHitStop(state, events);
    state.tick++;
    return;
  }
  rulesPre(state, events);
  updateSpecials(state, events);
  updateControllers(state, events);
  integrate(state, events);
  updateBall(state, events);
  resolveContacts(state, events);
  rulesPost(state, events);
  updateCameraHint(state);
  state.tick++;
};
